/**
 * Access & Session Telemetry — Fase 0
 *
 * Fire-and-forget helpers that emit normalized events to `analytics_events`
 * with consistent metadata: { source, platform, display_mode, ... }.
 *
 * Source attribution is computed once per tab and persisted in sessionStorage
 * so every downstream event shares the same value.
 *
 * NEVER throws, NEVER blocks UI. Analytics must never break the app.
 */

import { supabase } from "@/integrations/supabase/client";

const SOURCE_KEY = "mblearn_access_source";
const APP_OPENED_KEY = "mblearn_app_opened_emitted";
const PENDING_ACCESS_EVENTS_KEY = "mblearn_pending_access_events";

export type AccessSource = "pwa" | "email" | "browser" | "native" | "unknown";
export type DisplayMode = "standalone" | "browser" | "minimal-ui" | "fullscreen" | "unknown";
export type Platform = "ios" | "android" | "desktop" | "unknown";

export interface AccessContext {
  source: AccessSource;
  display_mode: DisplayMode;
  platform: Platform;
}

interface PendingAccessEvent {
  event_name: AccessEventName;
  screen: string | null;
  metadata: Record<string, unknown>;
}

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1)) {
    return "ios";
  }
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

function detectDisplayMode(): DisplayMode {
  if (typeof window === "undefined" || !window.matchMedia) return "unknown";
  if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
  if (window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen";
  if (window.matchMedia("(display-mode: minimal-ui)").matches) return "minimal-ui";
  if ((window.navigator as any).standalone === true) return "standalone";
  return "browser";
}

function detectNative(): boolean {
  return typeof window !== "undefined" && Boolean((window as any).Capacitor?.isNativePlatform?.());
}

/**
 * Resolve & cache the access source for this tab/session.
 * Priority:
 *  1. Capacitor native bridge → "native"
 *  2. URL ?source=pwa → "pwa"
 *  3. URL ?utm_source=email (or any utm_source containing "email") → "email"
 *  4. display-mode: standalone (installed PWA) → "pwa"
 *  5. otherwise → "browser"
 */
export function resolveSource(): AccessSource {
  try {
    const cached = sessionStorage.getItem(SOURCE_KEY) as AccessSource | null;
    if (cached) return cached;
  } catch {}

  let source: AccessSource = "browser";

  if (detectNative()) {
    source = "native";
  } else if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const explicit = (params.get("source") || "").toLowerCase();
    const utm = (params.get("utm_source") || "").toLowerCase();

    if (explicit === "pwa") source = "pwa";
    else if (explicit === "email" || utm.includes("email")) source = "email";
    else if (detectDisplayMode() === "standalone") source = "pwa";
  }

  try {
    sessionStorage.setItem(SOURCE_KEY, source);
  } catch {}

  return source;
}

export function getAccessContext(): AccessContext {
  return {
    source: resolveSource(),
    display_mode: detectDisplayMode(),
    platform: detectPlatform(),
  };
}

/**
 * Fase 0 access/session event names. Kept as a string union so the EventName
 * type in analytics.ts can stay backward-compatible without a refactor.
 */
export type AccessEventName =
  | "app_opened"
  | "session_lost_unexpected"
  | "session_invalidated_by_other_device"
  | "session_validation_failed"
  | "pwa_install_prompted"
  | "pwa_installed"
  | "version_mismatch_detected"
  | "forced_reload_applied"
  // ── OTP flow (Fase 1) ────────────────────────────────────────────────
  | "otp_requested"
  | "otp_verified"
  | "otp_failed"
  | "otp_resent"
  | "otp_expired"
  | "pwa_install_from_email_cta";

function readPendingEvents(): PendingAccessEvent[] {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(PENDING_ACCESS_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePendingEvents(events: PendingAccessEvent[]) {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (events.length === 0) {
      sessionStorage.removeItem(PENDING_ACCESS_EVENTS_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_ACCESS_EVENTS_KEY, JSON.stringify(events.slice(-20)));
  } catch {}
}

function queuePendingEvent(eventName: AccessEventName, screen?: string, metadata?: Record<string, unknown>) {
  const events = readPendingEvents();
  events.push({
    event_name: eventName,
    screen: screen ?? null,
    metadata: { ...getAccessContext(), ...(metadata ?? {}) },
  });
  writePendingEvents(events);
}

/**
 * Fire-and-forget access event. Always merges the resolved access context
 * into metadata so the materialized view can segment by source/platform.
 */
export function trackAccessEvent(
  eventName: AccessEventName,
  screen?: string,
  metadata?: Record<string, unknown>
) {
  setTimeout(async () => {
    try {
      const ctx = getAccessContext();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        queuePendingEvent(eventName, screen, metadata);
        return;
      }

      const pendingEvents = readPendingEvents();
      if (pendingEvents.length > 0) {
        await supabase.from("analytics_events" as any).insert(
          pendingEvents.map((pending) => ({
            user_id: session.user.id,
            event_name: pending.event_name,
            screen: pending.screen,
            metadata: pending.metadata,
          })),
        );
        writePendingEvents([]);
      }

      await supabase.from("analytics_events" as any).insert({
        user_id: session?.user?.id ?? null,
        event_name: eventName,
        screen: screen ?? null,
        metadata: { ...ctx, ...(metadata ?? {}) },
      });
    } catch {
      // Silent — telemetry must never break the app
    }
  }, 0);
}

/**
 * Emit `app_opened` at most once per tab session.
 * Called from the useSourceTracking hook on mount.
 */
export function trackAppOpenedOnce() {
  try {
    if (sessionStorage.getItem(APP_OPENED_KEY) === "1") return;
    sessionStorage.setItem(APP_OPENED_KEY, "1");
  } catch {
    // If sessionStorage is unavailable, fall through and emit anyway.
  }

  trackAccessEvent("app_opened", typeof window !== "undefined" ? window.location.pathname : undefined, {
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
  });
}
